import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Wallet, Activity, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { positionsAPI, accountAPI, historyAPI } from '../../lib/api';
import { subscribeMultiTicker, ALL_SYMBOLS } from '../../lib/market';
import { SYMBOLS, fmtPrice } from '../../lib/binance';
import { useAuth } from '../../context/AuthContext';

const StatCard = ({ label, value, sub, icon: Ico, positive }) => (
  <div className="p-5 rounded-xl border border-[#1a2823] bg-[#0a1210]">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
      <div className="w-8 h-8 rounded-lg bg-[#00e676]/10 border border-[#00e676]/20 flex items-center justify-center text-[#00e676]">
        <Ico size={14}/>
      </div>
    </div>
    <div className="text-2xl font-extrabold text-white mono">{value}</div>
    {sub && <div className={`mt-1 text-xs flex items-center gap-1 ${positive ? 'text-[#00e676]' : 'text-red-400'}`}>
      {positive ? <TrendingUp size={11}/> : <TrendingDown size={11}/>} {sub}
    </div>}
  </div>
);

const BigChart = () => {
  const points = Array.from({ length: 60 }, (_, i) => {
    const x = i * 15;
    const base = 120;
    const wave = Math.sin(i * 0.25) * 35 + Math.cos(i * 0.5) * 15 + i * 0.4;
    return `${x},${base - wave}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 900 200" className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="eqGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#00e676" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#00e676" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polyline points={points} fill="none" stroke="#00e676" strokeWidth="2"/>
      <polygon points={`${points} 900,200 0,200`} fill="url(#eqGrad)"/>
    </svg>
  );
};

const Overview = () => {
  const { user } = useAuth();
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [prices, setPrices] = useState({});
  const [recentTrades, setRecentTrades] = useState([]);

  useEffect(() => {
    positionsAPI.list().then(setPositions).catch(() => {});
    accountAPI.summary().then(setSummary).catch(() => {});
    historyAPI.list(5).then(setRecentTrades).catch(() => {});
  }, []);

  useEffect(() => {
    // Provider-aware live prices: the crypto watchlist AND every open-position
    // symbol (metals, forex, indices, stocks) — so their P&L updates live,
    // not just crypto. This fixes frozen P&L on non-crypto positions.
    const subs = [];
    const add = (a) => { if (a && !subs.some(x => x.symbol === a.symbol)) subs.push(a); };
    SYMBOLS.forEach(s => add(ALL_SYMBOLS.find(a => a.symbol === s.symbol)));
    positions.forEach(p => add(ALL_SYMBOLS.find(a => a.symbol === p.symbol)));
    if (subs.length === 0) return;
    const ws = subscribeMultiTicker(subs, (t) => {
      setPrices(prev => ({ ...prev, [t.symbol]: { ...(prev[t.symbol] || {}), ...t } }));
    });
    return () => { try { ws.close(); } catch {} };
  }, [positions]);

  const positionsWithPnL = positions.map(p => {
    const curr = prices[p.symbol]?.last || p.open_price;
    const diff = p.side === 'Buy' ? (curr - p.open_price) : (p.open_price - curr);
    const lev = p.leverage || user?.leverage || 1;
    return { ...p, currentPrice: curr, pnl: diff * p.volume * lev };
  });
  const floatingPnL = positionsWithPnL.reduce((a, p) => a + p.pnl, 0);
  const balance = summary?.balance ?? user?.balance ?? 0;
  const equity = balance + floatingPnL;

  const watchSyms = SYMBOLS.slice(0, 6);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Welcome, {user?.name || 'Trader'}</h1>
        <p className="text-sm text-gray-400">Your account snapshot and open activity.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Balance" value={`$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub="Cash available" positive icon={Wallet}/>
        <StatCard label="Equity" value={`$${equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={`${floatingPnL >= 0 ? '+' : ''}$${floatingPnL.toFixed(2)} floating`} positive={floatingPnL >= 0} icon={Activity}/>
        <StatCard label="Open Positions" value={positions.length} icon={Wallet}/>
        <StatCard label="Realised P&L" value={`${(summary?.realised_pnl ?? 0) >= 0 ? '+' : ''}$${(summary?.realised_pnl ?? 0).toFixed(2)}`} sub={`${summary?.closed_trades ?? 0} closed`} positive={(summary?.realised_pnl ?? 0) >= 0} icon={TrendingUp}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-bold">Equity Curve</h3>
              <p className="text-xs text-gray-400">Last 30 days</p>
            </div>
            <div className="flex gap-1">
              {['1D', '1W', '1M', '3M', 'YTD'].map((t, i) => (
                <button key={t} className={`px-3 py-1 rounded-md text-xs font-semibold ${i === 2 ? 'bg-[#00e676]/15 text-[#00e676] border border-[#00e676]/30' : 'text-gray-400 hover:text-white'}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="h-56"><BigChart/></div>
        </div>

        <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold">Watchlist</h3>
            <Link to="/dashboard/trade" className="text-xs text-[#00e676] hover:underline flex items-center gap-1">Trade <ChevronRight size={12}/></Link>
          </div>
          <div className="space-y-2">
            {watchSyms.map(s => {
              const p = prices[s.symbol];
              return (
                <div key={s.symbol} className="flex items-center justify-between py-2 border-b border-[#141f1b] last:border-0">
                  <div>
                    <div className="text-sm text-white font-semibold">{s.display}</div>
                    <div className="text-[10px] text-gray-500">Binance Spot</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white mono">{p ? fmtPrice(p.last, s.pricePrecision) : '—'}</div>
                    <div className={`text-[10px] mono ${(p?.changePct ?? 0) >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>{p?.changePct >= 0 ? '+' : ''}{p?.changePct?.toFixed(2) ?? '0.00'}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold">Open Positions</h3>
          <Link to="/dashboard/trade" className="text-xs text-[#00e676] hover:underline flex items-center gap-1">Terminal <ChevronRight size={12}/></Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="py-2 font-medium">Symbol</th>
                <th className="py-2 font-medium">Side</th>
                <th className="py-2 font-medium">Volume</th>
                <th className="py-2 font-medium">Open</th>
                <th className="py-2 font-medium">Mark</th>
                <th className="py-2 font-medium text-right">P&L</th>
              </tr>
            </thead>
            <tbody>
              {positionsWithPnL.map(p => (
                <tr key={p.id} className="border-t border-[#141f1b]">
                  <td className="py-3 text-white font-semibold">{p.display || p.symbol}</td>
                  <td><span className={`px-2 py-0.5 rounded text-xs font-semibold ${p.side === 'Buy' ? 'bg-[#00e676]/15 text-[#00e676]' : 'bg-red-500/15 text-red-400'}`}>{p.side}</span></td>
                  <td className="mono text-gray-300">{p.volume}</td>
                  <td className="mono text-gray-300">{Number(p.open_price).toFixed(2)}</td>
                  <td className="mono text-white">{Number(p.currentPrice).toFixed(2)}</td>
                  <td className={`mono font-semibold text-right ${p.pnl >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>{p.pnl >= 0 ? '+' : ''}${p.pnl.toFixed(2)}</td>
                </tr>
              ))}
              {positionsWithPnL.length === 0 && (
                <tr><td colSpan="6" className="py-8 text-center text-sm text-gray-500">No open positions. <Link to="/dashboard/trade" className="text-[#00e676] hover:underline">Open the trade terminal</Link>.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Overview;
